/**
 * Gambrinus Malting — Armstrong, British Columbia, Canada.
 *
 * The maker publishes a colour **band** per malt, in °L, SRM and EBC together,
 * alongside a maximum moisture, a total protein and a suggested usage rate.
 * Each entry takes the **mid-point of the published band**, because the
 * mid-point is the only figure in a range that is not a bound, and a bound is
 * the wrong number to design a recipe against.
 *
 * ## Three rows are `spec-sheet` and four are not
 *
 * Gambrinus publishes an extract minimum and a diastatic power for three of
 * its base malts — Pilsner, ESB Pale and IPA Malt — and for nothing else. The
 * Honey, Munich and Vienna pages print colour, moisture, protein and usage
 * rate, and stop. Those four are marked `representative`: colour, moisture
 * and usage rate are the maker's, and the extract and diastatic power are the
 * median this catalog already carries for the class. A malt that can carry a
 * grist has to state both, and a number invented here would read as published.
 *
 * ## Honey Malt is the one worth reading twice
 *
 * It is the original — the malt every other "honey malt" is imitating — and it
 * is commonly quoted at 25 °L and 15 % of the grist. The maker publishes
 * **15.3–22.7 °L** and **up to 10 %**. This entry follows the maker.
 *
 * @see https://gambrinusmalting.com/products/
 */

import type { Fermentable } from '../../schema/fermentable';

/** Gambrinus Malting fermentables. @see Fermentable */
export const gambrinusFermentables: readonly Fermentable[] = [
  {
    id: 'gambrinus-pilsner',
    name: 'Pilsner Malt',
    maker: 'Gambrinus Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 5.5,
    colorLovibond: 1.7,
    colorEbc: 3.3,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Prairie two-row taken pale and left enzyme-rich. A clean, unassertive floor under lagers and Kölsch, with diastatic power to spare for flaked adjuncts.',
    tags: ['base', 'lager', 'pilsner', 'canadian'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gambrinus-esb-pale',
    name: 'ESB Pale Malt',
    maker: 'Gambrinus Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 3,
    colorEbc: 6.8,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted to an English schedule rather than a Canadian one: biscuit and toasted bread over prairie barley. Built for bitters, milds and porters, and unusually enzymic for a malt of its character.',
    tags: ['base', 'pale-ale', 'english', 'biscuit', 'canadian'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gambrinus-ipa-malt',
    name: 'IPA Malt',
    maker: 'Gambrinus Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 2.2,
    colorEbc: 4.6,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Color splits the difference between pilsner and pale ale malt, kilned to a clean bread, toast and faint nuttiness. Built to give West Coast and hazy IPAs enough sweetness and body to carry heavy dry-hopping, and enzymic enough to make up the whole grist.',
    tags: ['base', 'ipa', 'pale-ale', 'canadian'],
    url: 'https://gambrinusmalting.com/gambrinus-ipa-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'gambrinus-honey',
    name: 'Honey Malt',
    maker: 'Gambrinus Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 19,
    colorEbc: 49.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The original honey malt, and still the benchmark: intense honeyed sweetness and a nutty malt depth, with none of the astringency a crystal malt of the same color would bring. A little goes a long way.',
    tags: ['honey', 'sweet', 'nutty', 'specialty', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'gambrinus-vienna',
    name: 'Vienna Malt',
    maker: 'Gambrinus Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 5,
    colorEbc: 12.1,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Golden amber, with warm brioche and lightly toasted grain. Enough on its own for a Vienna lager, and a way to give a pale beer some backbone without reaching for crystal.',
    tags: ['vienna', 'lager', 'toasty', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'gambrinus-munich-light',
    name: 'Munich Light',
    maker: 'Gambrinus Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 9,
    colorEbc: 22.7,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Deep malty sweetness and fresh crusty bread, at a color you can build an entire grist from. Keeps enough diastatic power to convert itself.',
    tags: ['munich', 'malty', 'bread', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'gambrinus-munich-dark',
    name: 'Munich Dark',
    maker: 'Gambrinus Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 24.6,
    colorEbc: 64,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Heavy toffee and dark bread crust. Kilned far enough that it brings little enzyme of its own, so it rides on a base malt rather than replacing one.',
    tags: ['munich', 'malty', 'toffee', 'canadian'],
    provenance: 'representative',
  },
];
